import { useCallback, useEffect, useMemo, useState } from 'react'
import { Checkbox } from '~/shadcn/ui/form/checkbox'
import { Field } from '~/shadcn/ui/field'
import Analytics from '~/@lib_integrations/analytics/analytics'
import { centsToEur, eurToCents } from '~/@lib/utils/money.utils'
import { formatDate } from '~/@lib/utils/date.utils'
import Finance from '~/@lib/utils/finance.utils'
import { CurrencyFormatter } from '~/@mid_ui/currency-formatter/CurrencyFormatter'
import { NavigationStackStyled } from '~/@mid_ui/navigation/NavigationStackStyled'
import { TextTooltip } from '~/@mid_ui/text-tooltip/TextTooltip'
import { SliderStyled } from '~/@mid_ui/slide-styled/SliderStyled'
import CompoundingYieldIcon from '~/@mid_ui/icons/CompoundingYieldIcon'
import { FormControlLabelStyled } from '~/@mid_ui-form/form-control-label-styled/FormControlLabelStyled'
import { Text } from '~/@mid_ui/typography/Text'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import { HeadingOne } from '~/@mid_ui/typography/HeadingOne'
import { useUser } from '~/@biz_account/useUser/useUser'
import { ProjectCompoundedRevenueChart } from '../../project-detail/project-detail_ui/project-compounded-revenue-chart/ProjectCompoundedRevenueChart'
import { useProjectInvestmentProjection } from '../../project-detail/project-detail_ui/project-detail-chart/useProjectInvestmentProjection'
import { BackButton } from '../../project-detail/project-detail_ui/back-button/BackButton'
import {
  BoosterProjectTooltip,
  BoosterTooltipContent,
} from '../../projects/_projects_ui/booster-project/BoosterProjectTooltip'
import { HotBonusWrapper } from '../../projects/_projects_ui/hot-bonus/HotBonusWrapper'
import { HotBonusComponent } from '../../projects/_projects_ui/hot-bonus/HotBonusComponent'
import { ProjectPreviewImageAndPopup } from '../../projects/_projects_ui/project-preview-and-label/ProjectPreviewImageAndPopup'
import { getWalletDisponibleFundsFixed } from '../../_utils/walletDisponibleFunds.utils'
import { getPayoutDate } from '../../project-detail/v1/utils'
import type { ProjectSchema } from '../../projects/_project_types/project.types'
import { OrderFeeImunity } from '../../fee-immunity/order-fee-immunity/OrderFeeImunity'
import { FeeImmunityTooltipContent } from '../../fee-immunity/FeeImmunityTooltipContent'
import { RevenueFeeTooltip } from '../_order_ui/revenue-fee-tooltip/RevenueFeeTooltip'
import { OrderAgreementDialog } from '../order-agreement/OrderAgreementDialog'
import type { AgreementPreviewRequest } from '../_order_types/order.types'
import { InvestmentInput } from '../_order_ui/investment-input/InvestmentInput'
import { useInvestmentInput } from '../_order_ui/investment-input/useInvestmentInput'
import { WrapperStyled } from '../_order_ui/wrappers/OrderWrapper'
import { InvestmentWrapper } from '../_order_ui/wrappers/InvestmentWrapper'
import { InvestmentAttribute } from '../_order_ui/wrappers/InvestmentAttribute'
import { InvestmentAttributeLabel } from '../_order_ui/wrappers/InvestmentAttributeLabel'
import { InvestmentAttributeCurrency } from '../_order_ui/wrappers/InvestmentAttributeCurrency'
import { InvestorFunds } from '../_order_ui/investor-funds/InvestorFunds'
import {
  DEFAULT_MIN_ORDER_AMOUNT,
  useOrderOfferDataQuery,
} from './useOrderOfferDataQuery'
import { Button } from '~/shadcn/ui/button/button'
import { useInvestmentReservationWithdrawableSetting } from '~/@pages_account-portfolio/investments/investments_ui/withdraw-investment-tooltip/useInvestmentReservationWithdrawableSetting'
import { cn } from '~/shadcn/lib/utils'
import { useFeeImmunity } from '../../fee-immunity/useFeeImmunity'
import {
  calculateFeeSaving,
  formatYieldValue,
} from '../../fee-immunity/feeImmunityDisplay.utils'
import { useRevenueFee } from '~/@pages_investing/_utils/revenue-fee/useRevenueFee'
import { ProjectPaymentTypeLabel } from '~/@pages_investing/projects/_projects_ui/project-payment-type-label/ProjectPaymentTypeLabel'
import { ProjectPaymentTypeTooltip } from '~/@pages_investing/projects/_projects_ui/project-payment-type-label/ProjectPaymentTypeTooltip'
import { getProjectPaymentTypeLabel } from '~/@pages_investing/projects/_projects_ui/project-payment-type-label/getProjectPaymentTypeLabel'

type OrderFormProps = {
  project: ProjectSchema
  /** Počet investícií usera pred podpisom; `undefined` = nevieme (načítava sa / chyba). */
  onOrderCreated: () => void
}

export const OrderForm = ({ project, onOrderCreated }: OrderFormProps) => {
  const { user } = useUser()
  const { hasFeeImmunity } = useFeeImmunity()
  const { calculateFeeNettoAmount } = useRevenueFee()
  const { fee: realFee, calculateFeeNettoAmount: calculateRealFeeNettoAmount } =
    useRevenueFee(true)
  const isDesktop = useIsDesktop()

  const { data: orderOfferData, reloadOrderOffer } = useOrderOfferDataQuery({
    businessId: project.businessId.toString(),
    stageNumber: project.stageNumber.toString(),
  })

  const { investmentReservationWithdrawableSetting } =
    useInvestmentReservationWithdrawableSetting()
  const withdrawalWindowDurationDays =
    investmentReservationWithdrawableSetting?.withdrawWindowDurationDays ?? 0

  const [riskAgreed, setRiskAgreed] = useState(false)
  const [periodAgreed, setPeriodAgreed] = useState(false)
  const [showOrderAgreementDialog, setShowOrderAgreementDialog] =
    useState(false)

  const toggleRiskAgreed = () => setRiskAgreed((v) => !v)
  const togglePeriodAgreed = () => setPeriodAgreed((v) => !v)

  const disponibleFundsFixed = getWalletDisponibleFundsFixed(
    user?.investorWallet ?? null
  )

  const [availableFutureRevenueFixed, setAvailableFutureRevenueFixed] =
    useState(orderOfferData?.availableFutureRevenueFixed || 0)
  const [useDisponibleFunds, setUseDisponibleFunds] = useState(
    disponibleFundsFixed > 0 || availableFutureRevenueFixed === 0
  )
  const [useFutureRevenue, setUseFutureRevenue] = useState(
    availableFutureRevenueFixed > 0
  )

  const getAvailableFundsFixed = () => {
    const funds = [
      useFutureRevenue ? availableFutureRevenueFixed : 0,
      useDisponibleFunds ? disponibleFundsFixed : 0,
    ]
    return funds.reduce((acc, v) => acc + v, 0)
  }

  const availableFundsFixed = getAvailableFundsFixed()

  const toggleUseDisponibleFunds = () =>
    setUseDisponibleFunds((oldValue) => {
      if (!useFutureRevenue) {
        return true
      }
      return !oldValue
    })

  const toggleUseFutureRevenue = () =>
    setUseFutureRevenue((oldValue) => {
      if (!useDisponibleFunds) return true

      return !oldValue
    })

  const minOrder = Math.max(DEFAULT_MIN_ORDER_AMOUNT, project?.amountMin || 0)
  const maxOrder = useDisponibleFunds
    ? Math.max(centsToEur(availableFundsFixed), project?.amountMax || 0)
    : centsToEur(availableFutureRevenueFixed)

  const {
    investment,
    investmentInputValue,
    sliderValue,
    handleSliderChange,
    handleBlur,
    handleChange,
    // specialBonus,
    // setSpecialBonus,
  } = useInvestmentInput({
    minOrder,
    maxOrder,
  })

  const { isProjectYieldCompoundingAnnualy, getProjectInvestmentChartData } =
    useProjectInvestmentProjection(project)

  const { getCompoundedAverages } = useProjectInvestmentProjection(
    project,
    true
  )

  const { chartData, compoundedAverages } = useMemo(() => {
    if (!isProjectYieldCompoundingAnnualy)
      return {
        chartData: null,
      }

    return {
      chartData: getProjectInvestmentChartData(sliderValue),
      compoundedAverages: getCompoundedAverages(sliderValue),
    }
  }, [
    isProjectYieldCompoundingAnnualy,
    getProjectInvestmentChartData,
    sliderValue,
    getCompoundedAverages,
  ])

  const handleOrderCreated = useCallback(
    (payload: AgreementPreviewRequest) => {
      Analytics.track('new-investment-step3-finished', {
        usedDisponibleFunds: centsToEur(payload.principalFixed.disponibleFunds),
        usedFutureRevenue: centsToEur(payload.principalFixed.futureRevenue),
        businessId: project.businessId,
        stageNumber: project.stageNumber,
      })
      onOrderCreated()
    },
    [onOrderCreated, project.businessId, project.stageNumber]
  )

  // TODO: WOF campaign
  // const {
  //   initialized,
  //   campaignSettings,
  //   investorHasBonusCode,
  //   investorInvestedWithBonus,
  //   generateWheelOfFortuneBonusCodeForEmail,
  // } = useWheelOfFortuneCampaign()
  // const [bonusCode, setBonusCode] = useState('')

  // const specialBonusCalculated = (() => {
  //   if (campaignSettings == null) return null
  //   if (!campaignSettings.isCampaignRunning) return null
  //   if (campaignSettings.minInvestmentAmount > investment) return null
  //   return specialBonus
  // })()

  // const handleSetInvestorBonusCode = async () => {
  //   if (campaignSettings == null) return

  //   setBonusCode(campaignSettings.code)
  //   setSpecialBonus({
  //     code: campaignSettings.code,
  //     amount: campaignSettings.bonusAmount,
  //   })

  //   if (user == null) return
  //   await generateWheelOfFortuneBonusCodeForEmail(user.profile.email)
  // }

  // const investorCanClaimCode = (() => {
  //   if (campaignSettings == null) return false
  //   if (disponibleFundsFixed * 100 < campaignSettings.minInvestmentAmount)
  //     return false
  //   if (investment < campaignSettings.minInvestmentAmount) return false
  //   return true
  // })()

  // const handleAutosetInvestorBonusCode = () => {
  //   if (campaignSettings == null || investorInvestedWithBonus === true) {
  //     setBonusCode('')
  //     setSpecialBonus(null)
  //     return
  //   }
  //   if (investorHasBonusCode === true) {
  //     handleSetInvestorBonusCode()
  //   }
  // }

  // useEffect(() => {
  //   handleAutosetInvestorBonusCode()
  // }, [campaignSettings, investorHasBonusCode, investorInvestedWithBonus])

  const investmentFixed = eurToCents(investment)
  const usedDisponibleFundsFixed = useDisponibleFunds
    ? Math.min(disponibleFundsFixed, investmentFixed)
    : 0
  const usedFutureRevenueFixed = useFutureRevenue
    ? investmentFixed - usedDisponibleFundsFixed
    : 0

  const payoutDate = getPayoutDate(project)
  const payoutDateFormatted = payoutDate ? formatDate(payoutDate) : ''

  const availableFundsExceeded = availableFundsFixed < investmentFixed
  const minimumValueError =
    minOrder > investment ? `Minimálna investícia je ${minOrder} €.` : undefined
  const buttonDisabled =
    availableFundsExceeded ||
    !riskAgreed ||
    !periodAgreed ||
    minimumValueError != null

  const revenueData = orderOfferData?.offerData

  useEffect(() => {
    reloadOrderOffer(investment.toString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investment])

  useEffect(() => {
    const newAvailableFutureRevenueFixed =
      orderOfferData?.availableFutureRevenueFixed || 0

    setAvailableFutureRevenueFixed(newAvailableFutureRevenueFixed)
    setUseFutureRevenue(newAvailableFutureRevenueFixed > 0)
  }, [orderOfferData])

  const calculatedRevenueData = useMemo(() => {
    if (revenueData == null) return null

    const revenue =
      isProjectYieldCompoundingAnnualy && compoundedAverages != null
        ? Finance.round(
            compoundedAverages.compoundedYieldPerAnnumAverageTotalRevenue
          )
        : calculateRealFeeNettoAmount(revenueData.revenue)

    const bonusLastminutePercent = calculateFeeNettoAmount(
      revenueData.bonusLastminutePercent
    )

    const bonusLastminute = calculateFeeNettoAmount(revenueData.bonusLastminute)

    const bonusPrereservationPercent = calculateFeeNettoAmount(
      revenueData.bonusPrereservationPercent
    )

    const bonusPrereservation = calculateFeeNettoAmount(
      revenueData.bonusPrereservation
    )

    const bonusWelcomePercent = revenueData.bonusWelcomePercent
    const bonusWelcome = revenueData.bonusWelcome
    const reservationCouponValue = revenueData.reservationCouponValue
    const bonusAdditionalSpecial = revenueData.bonusAdditionalSpecial

    const totalYieldPerAnnumCalculated =
      isProjectYieldCompoundingAnnualy && compoundedAverages != null
        ? compoundedAverages.compoundedYieldPerAnnumAverageTotal
        : calculateRealFeeNettoAmount(revenueData.totalYieldPerAnnum)

    const revenueFeeApplied = revenueData.revenueFeeApplied
    const totalWithCouponValueCalculated =
      revenueData.amount +
      revenue +
      bonusLastminute +
      bonusPrereservation +
      bonusWelcome +
      bonusAdditionalSpecial +
      reservationCouponValue

    const feeSavingAmount =
      isProjectYieldCompoundingAnnualy && compoundedAverages != null
        ? hasFeeImmunity
          ? compoundedAverages.compoundedYieldPerAnnumAverageTotalReturnAmountBrutto -
            compoundedAverages.compoundedYieldPerAnnumAverageTotalReturnAmount
          : 0
        : hasFeeImmunity
          ? revenueData.totalWithCouponValue - totalWithCouponValueCalculated
          : 0
    const feeSavingPercent = calculateFeeSaving(
      project._calculatedValues.maxYieldValue > 0
        ? project._calculatedValues.maxYieldValue
        : project._calculatedValues.minYieldValue,
      realFee
    )

    return {
      revenue,
      bonusLastminutePercent,
      bonusLastminute,
      bonusPrereservationPercent,
      bonusPrereservation,
      bonusWelcomePercent,
      bonusWelcome,
      totalYieldPerAnnum: totalYieldPerAnnumCalculated,
      reservationCouponValue,
      bonusAdditionalSpecial,
      revenueFeeApplied,
      totalWithCouponValue: totalWithCouponValueCalculated + feeSavingAmount,
      feeSavingPercent:
        isProjectYieldCompoundingAnnualy && compoundedAverages != null
          ? Finance.round(feeSavingPercent)
          : Finance.round(
              revenueData.totalYieldPerAnnum - totalYieldPerAnnumCalculated
            ),
      feeSavingAmount: Finance.round(feeSavingAmount),
    }
  }, [
    revenueData,
    isProjectYieldCompoundingAnnualy,
    compoundedAverages,
    calculateFeeNettoAmount,
    calculateRealFeeNettoAmount,
    hasFeeImmunity,
    project._calculatedValues.maxYieldValue,
    project._calculatedValues.minYieldValue,
    realFee,
  ])

  if (calculatedRevenueData == null) return null

  const {
    revenue,
    bonusAdditionalSpecial,
    bonusPrereservationPercent,
    bonusPrereservation,
    bonusLastminutePercent,
    bonusLastminute,
    bonusWelcome,
    bonusWelcomePercent,
    totalYieldPerAnnum,
    reservationCouponValue,
    totalWithCouponValue,
    revenueFeeApplied,
    feeSavingPercent,
    feeSavingAmount,
  } = calculatedRevenueData

  console.log('feeSavingPercent', feeSavingPercent)

  return (
    <div translate="no">
      <WrapperStyled>
        <div className="max-w-[864px] w-full mx-auto">
          <div className="flex flex-col gap-10 pt-0 lg:pt-5">
            <NavigationStackStyled>
              <BackButton className="text-primary-foreground md:text-accent">
                {isDesktop && 'Späť na detail'}
              </BackButton>
            </NavigationStackStyled>
            <div className="flex flex-col gap-2 text-text-secondary">
              <Text className="font-semibold">
                Investičná príležitosť ID: {project.businessId}
              </Text>
              <HeadingOne className="text-3xl">
                Objednávka investície
              </HeadingOne>
              <div className="flex flex-row items-center gap-2.5">
                <ProjectPreviewImageAndPopup
                  project={project}
                  withPopup={false}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-[864px] w-full mx-auto">
          <InvestmentWrapper>
            {/* TODO: WOF campaign */}
            {/* {initialized === true &&
            campaignSettings != null &&
            campaignSettings.isCampaignRunning &&
            investorInvestedWithBonus === false && (
              <WheelOfFortuneInput
                id="wof-bonus-input"
                value={bonusCode}
                onBlur={handleBlur}
                darkTheme
                showInputDisclaimer
                continueSuccesText="Dokončite objednávku."
                onSetBonusCodeValue={() => handleSetInvestorBonusCode()}
                investmentOrder={{
                  investorCanClaimCode,
                  investorHasBonusCode,
                }}
              />
            )} */}
            <div className="flex flex-col gap-0 lg:gap-4">
              <HotBonusComponent
                {...project.hotBonus}
                WrapperComponent={HotBonusWrapper}
              />
              <div className="flex flex-col gap-2.5 lg:flex-row lg:justify-between">
                <div className="pt-3 flex flex-col gap-1">
                  <h2 className="text-2xl font-semibold leading-9">
                    Chcem investovať
                  </h2>

                  <InvestorFunds
                    showCheckBox={project.investmentFutureRevenueEnabled}
                    isChecked={useDisponibleFunds}
                    toggle={toggleUseDisponibleFunds}
                    funds={disponibleFundsFixed}
                    label="Disponibilné Prostriedky:"
                    tooltipContent="Aktuálny zostatok na mojom disponibilnom investičnom účte"
                  />

                  {project.investmentFutureRevenueEnabled && (
                    <InvestorFunds
                      showCheckBox={true}
                      isChecked={useFutureRevenue}
                      toggle={toggleUseFutureRevenue}
                      funds={availableFutureRevenueFixed}
                      label="Očakávané prostriedky:"
                      tooltipContent={<BoosterTooltipContent />}
                      boosterComponent={
                        availableFutureRevenueFixed > 100 &&
                        user &&
                        user.investorWallet && (
                          <BoosterProjectTooltip
                            investorWallet={user.investorWallet}
                            availableFutureRevenueFixed={
                              availableFutureRevenueFixed
                            }
                          />
                        )
                      }
                    />
                  )}
                </div>
                <InvestmentInput
                  id={'investment-input'}
                  value={investmentInputValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  minimumValueError={minimumValueError}
                  availableFundsExceeded={availableFundsExceeded}
                  usedFutureRevenueFixed={usedFutureRevenueFixed}
                  totalYieldPerAnnum={totalYieldPerAnnum}
                  revenueFeeApplied={revenueFeeApplied}
                  isCompoundingAnnualy={isProjectYieldCompoundingAnnualy}
                />
              </div>
              <div className="mt-5 flex flex-col gap-2.5">
                <SliderStyled
                  step={0.01}
                  min={Math.log10(minOrder)}
                  max={Math.log10(maxOrder)}
                  value={[Math.log10(sliderValue)]}
                  onChange={handleSliderChange}
                  thumbcoloroverride="#e1e1e1"
                  className={cn(
                    availableFundsExceeded
                      ? "**:data-[slot='slider-range']:bg-border-strong"
                      : usedFutureRevenueFixed > 0
                        ? "**:data-[slot='slider-range']:bg-secondary"
                        : undefined
                  )}
                />
                <div className="flex flex-row justify-between px-2">
                  <CurrencyFormatter
                    className="text-sm lg:text-md text-muted font-semibold"
                    value={minOrder}
                  />
                  <CurrencyFormatter
                    className="text-sm lg:text-md text-muted font-semibold"
                    value={maxOrder}
                  />
                </div>
              </div>

              <OrderFeeImunity currentValue={investment} />
            </div>

            {chartData != null && (
              <div className="mt-5">
                <ProjectCompoundedRevenueChart
                  chartData={chartData}
                  variant="black"
                />
              </div>
            )}

            <div className="w-full flex flex-col gap-0">
              <InvestmentAttribute className="flex items-center">
                {isProjectYieldCompoundingAnnualy &&
                compoundedAverages != null ? (
                  <>
                    <InvestmentAttributeLabel className="flex flex-row items-center gap-1">
                      <span>Výnos</span>
                      <span>{formatYieldValue(totalYieldPerAnnum)} %</span>
                      <CompoundingYieldIcon size={18} percentColor="#e3e3e3" />
                      <ProjectPaymentTypeTooltip
                        paymentTypeLabel={getProjectPaymentTypeLabel(
                          project.paymentTypeId,
                          isProjectYieldCompoundingAnnualy
                        )}
                      />
                    </InvestmentAttributeLabel>
                    <div className="flex flex-col gap-1 lg:flex-row">
                      <InvestmentAttributeCurrency value={revenue} />
                    </div>
                  </>
                ) : (
                  <>
                    <InvestmentAttributeLabel className="flex flex-row items-center gap-1">
                      <span>Výnos</span>
                      <span>{formatYieldValue(totalYieldPerAnnum)} % p.a.</span>

                      <RevenueFeeTooltip
                        revenueFeeApplied={revenueFeeApplied}
                      />
                    </InvestmentAttributeLabel>
                    <InvestmentAttributeCurrency value={revenue} />
                  </>
                )}
              </InvestmentAttribute>
              {hasFeeImmunity && feeSavingPercent > 0 && (
                <InvestmentAttribute>
                  <div className="flex items-center flex-row gap-1">
                    <InvestmentAttributeLabel className="text-[#B061FF]">
                      Poplatková imunita:{` `}
                      {feeSavingPercent}
                      {isProjectYieldCompoundingAnnualy ? (
                        <CompoundingYieldIcon
                          size={14}
                          percentColor="#B061FF"
                          arrowColor="#B061FF"
                          className="inline-block align-middle mx-0.5"
                        />
                      ) : (
                        ' %'
                      )}
                    </InvestmentAttributeLabel>
                    <TextTooltip text={FeeImmunityTooltipContent()} />
                  </div>
                  <InvestmentAttributeCurrency
                    className="text-[#B061FF]"
                    value={feeSavingAmount}
                  />
                </InvestmentAttribute>
              )}
              {bonusPrereservationPercent > 0 && (
                <InvestmentAttribute>
                  <InvestmentAttributeLabel className="text-[#B061FF]">
                    <div className="flex items-center flex-row gap-1">
                      Predrezervačný bonus {bonusPrereservationPercent} %
                      <RevenueFeeTooltip
                        revenueFeeApplied={revenueFeeApplied}
                      />
                    </div>
                  </InvestmentAttributeLabel>
                  <InvestmentAttributeCurrency
                    value={bonusPrereservation}
                    className="text-[#B061FF]"
                  />
                </InvestmentAttribute>
              )}
              {bonusLastminutePercent > 0 && (
                <InvestmentAttribute>
                  <InvestmentAttributeLabel className="text-[#F0A000]">
                    <div className="flex items-center flex-row gap-1">
                      Okamžitý bonus {bonusLastminutePercent} %
                      <RevenueFeeTooltip
                        revenueFeeApplied={revenueFeeApplied}
                      />
                    </div>
                  </InvestmentAttributeLabel>

                  <InvestmentAttributeCurrency
                    value={bonusLastminute}
                    className="text-[#F0A000]"
                  />
                </InvestmentAttribute>
              )}
              {!!bonusWelcomePercent && (
                <InvestmentAttribute>
                  <div className="flex items-center flex-row gap-1">
                    <InvestmentAttributeLabel className="text-[#de44de] gap-1">
                      Uvítací darček {bonusWelcomePercent} %
                      <TextTooltip text="Bonus pre nových investorov na základe doporučenia od affiliate partnera" />
                    </InvestmentAttributeLabel>
                  </div>
                  <InvestmentAttributeCurrency
                    value={bonusWelcome}
                    className="text-[#de44de]"
                  />
                </InvestmentAttribute>
              )}
              {!!reservationCouponValue && (
                <InvestmentAttribute>
                  <InvestmentAttributeLabel className="text-[#B061FF]">
                    Welcome Bonus {Finance.round(reservationCouponValue || 0)} %
                  </InvestmentAttributeLabel>
                  <InvestmentAttributeCurrency value={reservationCouponValue} />
                </InvestmentAttribute>
              )}
              {!!bonusAdditionalSpecial && (
                <InvestmentAttribute>
                  <div className="flex items-center flex-row gap-1">
                    <InvestmentAttributeLabel className="text-[#B061FF]">
                      Špeciálny bonus:
                    </InvestmentAttributeLabel>
                  </div>
                  <InvestmentAttributeCurrency value={bonusAdditionalSpecial} />
                </InvestmentAttribute>
              )}

              <InvestmentAttribute>
                {isProjectYieldCompoundingAnnualy &&
                compoundedAverages != null ? (
                  <>
                    <InvestmentAttributeLabel className="text-[#50F000]">
                      Vrátená čiastka:
                    </InvestmentAttributeLabel>
                    <div className="flex flex-col gap-1 lg:flex-row">
                      <InvestmentAttributeCurrency
                        className="text-[#50F000]"
                        value={totalWithCouponValue}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <InvestmentAttributeLabel className="text-[#50F000]">
                      Vrátená čiastka:
                    </InvestmentAttributeLabel>
                    <InvestmentAttributeCurrency
                      className="text-[#50F000]"
                      value={totalWithCouponValue}
                    />
                  </>
                )}
              </InvestmentAttribute>
              <InvestmentAttribute>
                <InvestmentAttributeLabel>Typ výnosu:</InvestmentAttributeLabel>
                <InvestmentAttributeLabel className="normal-case">
                  <ProjectPaymentTypeLabel
                    projectPaymentType={project.paymentTypeId}
                    isYieldCompounding={isProjectYieldCompoundingAnnualy}
                  />
                </InvestmentAttributeLabel>
              </InvestmentAttribute>
              <InvestmentAttribute className="border-b-0">
                <InvestmentAttributeLabel>
                  Dátum splatenia investície:
                </InvestmentAttributeLabel>
                <InvestmentAttributeLabel>
                  {payoutDateFormatted}
                </InvestmentAttributeLabel>
              </InvestmentAttribute>
            </div>

            <div className="flex flex-col gap-4 text-icon-default">
              <FormControlLabelStyled
                disabled={availableFundsExceeded || minimumValueError != null}
                className="cursor-pointer"
                control={
                  <Checkbox
                    checked={riskAgreed}
                    className="border-primary-foreground mt-1"
                    onCheckedChange={toggleRiskAgreed}
                    onKeyDown={(e) => e.key === 'Enter' && toggleRiskAgreed()}
                  />
                }
                label="Uvedomujem si riziko spojené s investovaním a akceptujem riziko straty všetkých investovaných peňažných prostriedkov."
              />
              <FormControlLabelStyled
                disabled={availableFundsExceeded || minimumValueError != null}
                className="cursor-pointer"
                control={
                  <Checkbox
                    checked={periodAgreed}
                    className="border-primary-foreground mt-1"
                    onCheckedChange={togglePeriodAgreed}
                    onKeyDown={(e) => e.key === 'Enter' && togglePeriodAgreed()}
                  />
                }
                label={`Beriem na vedomie, že pri investovaní môžem využiť tzv. Ochrannú lehotu, ktorá začne plynúť prejavením záujmu investovať (podpísaním objednávky). Počas tejto ${
                  withdrawalWindowDurationDays
                }-dňovej lehoty môžem svoju objednávku zrušiť bez uvedenia dôvodu a bez akejkoľvek sankcie v sekcii „Moje investície“.`}
              />
            </div>

            <Field>
              <Button
                variant="accent"
                className=""
                disabled={buttonDisabled}
                onClick={() => {
                  Analytics.track('new-investment-step1-button-clicked')
                  setShowOrderAgreementDialog(true)
                }}>
                Dokončiť objednávku
              </Button>
            </Field>
          </InvestmentWrapper>
        </div>
      </WrapperStyled>
      {showOrderAgreementDialog && user && user.investorWallet && (
        <OrderAgreementDialog
          user={user}
          orderParams={{
            projectId: project._id,
            walletId: user.investorWallet._id,
            principalFixed: {
              disponibleFunds: usedDisponibleFundsFixed,
              futureRevenue: usedFutureRevenueFixed,
            },
          }}
          onClose={() => setShowOrderAgreementDialog(false)}
          onSuccess={handleOrderCreated}
        />
      )}
    </div>
  )
}
